import { ClickAwayListener } from '@mui/material';
import dateFormatter from '@virtus/common/utils/dateFormater';
import React, { PureComponent } from 'react';
import { Filter } from 'react-table';
import styled from 'styled-components';

export interface SelectFilterProps {
  readonly onChange: (event: any) => void;
  readonly filterValues: any[];
  readonly isBooleanFilter?: boolean;
}

export const selectFilterMethod = (filter: Filter, row: any) => {
  if ((row[filter.id] && !row._subRows && typeof row[filter.id] === 'string') || typeof row[filter.id] === 'boolean') {
    if (!filter.value.length) {
      return true;
    }

    const values = filter.value.map((v: string) => v.toLowerCase().trim());
    if (typeof row[filter.id] === 'boolean') {
      const rowValueInString: string = row[filter.id] ? 'yes' : 'no';
      return Boolean(values.indexOf(rowValueInString) !== -1);
    }
    if (values.indexOf(row[filter.id].toLowerCase().trim()) !== -1) {
      return true;
    }
    return false;
  }

  if (row._aggregated) {
    const values = filter.value.map((v: string) => v.toLowerCase().trim());
    if (
      row._subRows.filter(
        (subrow: any) => values.indexOf(subrow[filter.id] && subrow[filter.id].toLowerCase().trim()) !== -1,
      )
    ) {
      return true;
    }
  }
  if (!filter.value.length) {
    return true;
  }

  return false;
};

const formatValue = (value: string) => (Date.parse(value) ? dateFormatter(new Date(value)) : value);

export default class SelectFilter extends PureComponent<SelectFilterProps> {
  state = { open: false, selectedValues: [], filterValues: this.props.filterValues };

  getFilterValue = (currentValues: string[], value: string) => {
    if (this.isSelected(value)) {
      return currentValues.filter(e => e !== value);
    }
    return [...currentValues, ...[value]];
  };

  handleFilter = (event: any) => {
    const _newFilterValues = this.props.filterValues.filter(value =>
      value.toLowerCase().includes(event.target.value.toLowerCase()),
    );
    this.setState({ filterValues: _newFilterValues });
  };

  handleSelectOption = (value: string) => {
    const newValues = this.getFilterValue(this.state.selectedValues, value);
    this.props.onChange(newValues);
    this.setState({ selectedValues: newValues });
  };

  isSelected = (value: string) => Boolean(this.state.selectedValues.find(element => element === value));

  toggleSelect = () => {
    this.setState({
      open: !this.state.open,
      filterValues: this.props.filterValues,
    });
  };
  onClickAway = () =>
    this.setState({
      open: false,
    });

  handleShowAll = () => {
    this.props.onChange([]);
    this.setState({
      selectedValues: [],
      open: false,
      filterValues: this.props.filterValues,
    });
  };

  render() {
    const { filterValues } = this.state;
    if (!this.props.filterValues.length) return null;

    return (
      <ClickAwayListener data-testid="click-away-listener" onClickAway={this.onClickAway}>
        <SelectWrapper data-testid="select">
          <SelectLabel data-testid="select-toggle" onClick={this.toggleSelect}>
            Choose an option <ArrowIcon>{!this.state.open ? '▼' : '▲'}</ArrowIcon>
          </SelectLabel>
          {this.state.open && (
            <Select data-testid="select-picker">
              {!this.props.isBooleanFilter && <InputFilter data-testid="select-filter" onChange={this.handleFilter} />}
              <ShowAllButton data-testid="select-show-all" onClick={this.handleShowAll}>
                All
              </ShowAllButton>
              {filterValues.map((value: string) => (
                <Option key={value} onClick={() => this.handleSelectOption(value)}>
                  <Checkbox>{this.isSelected(value) ? '✓' : ' '}</Checkbox>
                  <OptionText>{formatValue(value)}</OptionText>
                </Option>
              ))}
            </Select>
          )}
        </SelectWrapper>
      </ClickAwayListener>
    );
  }
}

// Styled Components
const SelectWrapper = styled.div`
  position: absolute;
  width: 100%;
  padding-right: 10px;
`;

const SelectLabel = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  background-color: #fff !important;
  padding: 4px 5px !important;
  font-size: inherit !important;
  border-radius: 2px !important;
  font-weight: normal !important;
  outline: none !important;
  cursor: pointer !important;
  height: 28px;
`;

const ArrowIcon = styled.span`
  font-size: 10px;
`;

const InputFilter = styled.input`
  margin-top: 5px;
  width: 95%;
`;

const ShowAllButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin-left: 5px;
  margin-right: 5px;
  margin-top: 5px;
`;

const Select = styled.div`
  width: 100%;
  background-color: #fff;
  max-height: 200px;
  overflow-y: scroll;
  box-shadow: -1px 8px 12px -5px rgba(0, 0, 0, 0.2);
`;

const Option = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 5px 2px 5px 0px !important;
  padding: 0px !important;
  cursor: pointer !important;
  background-color: #fff;
`;

const OptionText = styled.div`
  flex: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: left;
`;

const Checkbox = styled.div`
  color: #0073ea !important;
  display: inline-block !important;
  background: #fff !important;
  font-weight: bold !important;
  border: 1px solid lightgrey !important;
  border-radius: 2px !important;
  width: 16px !important;
  height: 16px !important;
  margin-right: 5px !important;
  margin-left: 5px;
`;
